import {
  aNetworkBlock,
  anInsightConfig,
  EntityAutomationsQueryOperationQuery,
  EntityFormNotesQueryOperationQuery,
  EntityNote,
  InsightSeverityPyEnum,
  mockEntityAsideLookbackOperationQuery,
  mockEntityAsideQueryOperationQuery,
  mockEntityAsideUpdateSuppressionMutation,
  mockEntityAutomationsQueryOperationQuery,
  mockEntityFormNotesDeleteMutationMutation,
  mockEntityFormNotesQueryOperationQuery,
  mockEntityFormNotesUpdateMutationMutation,
  mockEntityInputCriticalityQueryOperationQuery,
  mockEntityInputTagsDeleteOperationMutation,
  mockEntityInputTagsQueryOperationQuery,
  mockEntityInputTagsUpdateOperationMutation,
  mockEntityNetworkBlocksQueryOperationQuery,
  mockEntityTypeQueryOperationQuery,
  mockExecuteEntityAutomationOperationMutation,
  mockUpdateEntityInputCriticalityMutation,
  EntityAsideQueryOperationQuery as Query,
  User,
} from '__generated__';
import { delay, HttpResponse } from 'msw';

import { DynamicFeatureFlag } from '@sbu/hooks.featureflag';
import { mockEnabledFeatureFlagsQuery } from '@sbu/hooks.featureflag/build/mocks';
import { mockPermissionsQuery } from '@sbu/hooks.permissions/build/mocks';
import {
  handlers as entityAuditLogHandlers,
  mockAuditLog,
} from '@sbu/views.entityauditlog/build/mocks';
import {
  handlers as entityMetadataHandlers,
  mockIPAddressDetails,
} from '@sbu/views.entitymetadata/build/mocks';
import { handlers as entityToggleSuppressHandlers } from '@sbu/views.entitytogglesuppress/build/mocks';

export const mockNetworkBlocksByIp = [
  aNetworkBlock({
    addressBlock: '10.100.10.10/24',
    id: '0',
    label: 'a very long label about a network block',
  }),
  aNetworkBlock({
    addressBlock: '172.114.16.10/24',
    id: '1',
    label: 'label',
  }),
  aNetworkBlock({
    addressBlock: '192.168.124.110/24',
    id: '2',
  }),
];

export const mockInsights: Query['entity']['insights']['objects'] = [
  {
    name: 'Persistence with execution',
    readableId: 'INSIGHT-17074',
    severity: InsightSeverityPyEnum.High,
    timestamp: new Date(Date.now() - 1000000).toISOString(),
    __typename: 'Insight',
  },
  {
    name: 'Persistence with execution',
    readableId: 'INSIGHT-17075',
    severity: InsightSeverityPyEnum.Low,
    timestamp: new Date(Date.now() - 2000000).toISOString(),
    __typename: 'Insight',
  },
  {
    name: 'Persistence with execution',
    readableId: 'INSIGHT-17076',
    severity: InsightSeverityPyEnum.Critical,
    timestamp: new Date(Date.now() - 3000000).toISOString(),
    __typename: 'Insight',
  },
  {
    name: 'Persistence with execution',
    readableId: 'INSIGHT-17077',
    severity: InsightSeverityPyEnum.Medium,
    timestamp: new Date(Date.now() - 4000000).toISOString(),
    __typename: 'Insight',
  },
  {
    name: 'Persistence with execution',
    readableId: 'INSIGHT-17078',
    severity: InsightSeverityPyEnum.High,
    timestamp: new Date(Date.now() - 5000000).toISOString(),
    __typename: 'Insight',
  },
];

export const mockSignals: Query['entity']['signals']['objects'] = [
  {
    created: '2022-10-14T01:07:32',
    name: 'Proofpoint TAP - User Received Email with Malware',
    id: 'f0791622-fbf9-525b-95f4-c0ee1a6bd1e2',
    __typename: 'Signal',
  },
  {
    created: '2022-10-13T17:22:47',
    name: 'HCThresholdRuleTest',
    id: '19062748-6bc9-57ae-a279-dba84233c17a',
    __typename: 'Signal',
  },
  {
    created: '2022-10-11T14:36:56',
    name: 'Proofpoint TAP - ',
    id: 'f0791622-fbf9-525b-95f4-c0ee1a6bd1e3',
    __typename: 'Signal',
  },
];

export let mockNotes: EntityFormNotesQueryOperationQuery['entityNotes'] = [
  {
    author: {
      __typename: 'User',
      displayName: 'Wonder Woman',
      id: '12344',
      role: 'blue team',
    },
    id: '0922',
    lastUpdated: '2022-10-15T10:14:56',
    note: 'This could be coming from a different country and not originating from United Kingdom',
    timestamp: '2022-10-15T10:14:56',
    __typename: 'EntityNote',
  },
  {
    author: {
      __typename: 'User',
      displayName: 'Dash Prospers',
      id: '12345',
      role: 'red team',
    },
    id: '0923',
    lastUpdated: '2022-10-15T13:20:56',
    note: 'This IP address belongs to me and was a test.',
    timestamp: '2022-10-15T13:20:56',
    __typename: 'EntityNote',
  },
  {
    author: {
      __typename: 'User',
      displayName: 'Bruce Wayne',
      id: '12346',
      role: 'yellow team',
    },
    id: '0924',
    lastUpdated: '2022-10-15T17:17:56',
    note: 'Thanks for adding stress to my workday. *sulks*',
    timestamp: '2022-10-15T17:17:56',
    __typename: 'EntityNote',
  },
  {
    author: {
      __typename: 'User',
      displayName: 'Clark Kent',
      id: '01237',
      role: 'purple team',
    },
    id: '0925',
    lastUpdated: '2022-10-15T19:17:56',
    note: 'Stop being passive aggressive, Batman. *eyes begins to glow*',
    timestamp: '2022-10-15T19:17:56',
    __typename: 'EntityNote',
  },
];

export const mockEntity: Query['entity'] = {
  auditLog: mockAuditLog.map((audit) => ({
    id: audit.id,
    __typename: 'EntityAuditLog',
  })),
  enrichments: [
    {
      reputation: 'Malicious',
      type: 'VirusTotal',
      normalizedIndicator: {
        id: 'testId1',
        indicator: 'testIndicator',
        source: 'Some Source',
        __typename: 'NormalizedIndicator',
      },
      __typename: 'Enrichment',
    },
    {
      reputation: 'NotFlagged',
      type: 'VirusTotal',
      normalizedIndicator: {
        id: 'testId2',
        indicator: 'testIndicator',
        source: 'Automation',
        __typename: 'NormalizedIndicator',
      },
      __typename: 'Enrichment',
    },
    {
      reputation: 'Suspicious',
      type: 'VirusTotal',
      normalizedIndicator: {
        id: 'testId3',
        indicator: 'testIndicator',
        source: 'Threat Intelligence',
        __typename: 'NormalizedIndicator',
      },
      __typename: 'Enrichment',
    },
    {
      reputation: 'Malicious',
      type: 'VirusTotal',
      normalizedIndicator: {
        id: 'testId4',
        indicator: 'testIndicator',
        source: null,
        __typename: 'NormalizedIndicator',
      },
      __typename: 'Enrichment',
    },
    {
      reputation: 'Suspicious',
      type: 'AbuseIPBD',
      __typename: 'Enrichment',
    },
    {
      reputation: null,
      type: 'RecordedFutures',
      __typename: 'Enrichment',
    },
  ],
  entityType: '_ip',
  entityTypeName: 'IP Address',
  id: '',
  insights: {
    objects: mockInsights,
    total: mockInsights.length,
    __typename: 'InsightPage',
  },
  ipAddressDetails: mockIPAddressDetails,
  isWhitelisted: true,
  suppressListsObj: [
    { id: '1', name: 'Test list 1', __typename: 'SuppressList' },
    { id: '2', name: 'Test list 2', __typename: 'SuppressList' },
  ],
  networkBlocks: mockNetworkBlocksByIp,
  notes: (mockNotes ?? []).map((note) => ({
    id: note.id,
    __typename: 'EntityNote',
  })),
  reputation: 'Malicious',
  sources: [
    {
      sourceType: 'DIRECT',
      entityGroupConfigurationName: 'Entity Group 1',
      __typename: 'EntityMetadataSource',
    },
    {
      sourceType: 'ENTITY_VALUE_GROUP',
      entityGroupConfigurationName: 'Entity Group 2',
      __typename: 'EntityMetadataSource',
    },
    {
      sourceType: 'INVENTORY',
      entityGroupConfigurationName: 'Entity Group 3',
      __typename: 'EntityMetadataSource',
    },
  ],
  signals: {
    objects: mockSignals,
    total: mockSignals.length,
    __typename: 'SignalPage',
  },
  value: '96.46.254.140',
  recentSignalSeverity: 11,
  __typename: 'EntityInterface',
};

let tagState: string[] = ['Tag1', 'Tag2', '_mitreAttackTactic:TA0005'];

export const mockAutomations: EntityAutomationsQueryOperationQuery['automations']['objects'] =
  [
    {
      __typename: 'Automation',
      cseResourceSubTypes: ['_ip'],
      description: 'Best Automation',
      enabled: true,
      executionTypes: ['ON_DEMAND'],
      id: '7a4a96ea-e3ce-4956-b9e3-fa6779f374f6',
      name: 'CSE IP Enrichment',
      playbookId: '62f4b5e159736d2edfd9dba7',
    },
    {
      __typename: 'Automation',
      cseResourceSubTypes: ['_ip'],
      description: '',
      enabled: false,
      executionTypes: ['ON_DEMAND'],
      id: 'ba4a96ea-33ce-4956-b9e3-fa6779f374f6',
      name: 'CSE IP Enrichment - Disabled',
      playbookId: 'f4d4b5e159736d2edfd9daa0',
    },
    {
      __typename: 'Automation',
      cseResourceSubTypes: ['_ip'],
      description: '',
      enabled: true,
      executionTypes: ['INSIGHT_CREATED'],
      id: 'ba4a96ea-33ce-4956-b9e3-fa6779f374f6',
      name: 'CSE IP Enrichment - Insight Created',
      playbookId: 'f4d4b5e159736d2edfd9daa0',
    },
  ];

export const handlers = [
  ...entityAuditLogHandlers,
  ...entityMetadataHandlers,
  ...entityToggleSuppressHandlers,

  mockEnabledFeatureFlagsQuery(
    async ({ query: _query, variables: _variables }) => {
      await delay(50);
      return HttpResponse.json({
        data: {
          enabledFeatureFlags: [
            DynamicFeatureFlag.AutomationService,
            DynamicFeatureFlag.SOARAutomaticService,
            DynamicFeatureFlag.threatIntelEntityEnrichment,
          ],
        },
      });
    }
  ),

  mockPermissionsQuery(async ({ query: _query, variables: _variables }) => {
    await delay();
    return HttpResponse.json({
      data: {
        currentUser: {
          permissions: [
            'cloud_soar_playbooks_access',
            'can_execute_automations',
          ],
        },
      },
    });
  }),

  mockEntityAsideQueryOperationQuery(async ({ query: _query, variables }) => {
    await delay(50);
    const { id } = variables;

    return HttpResponse.json({
      data: {
        __typename: 'Query',
        entity: {
          ...mockEntity,
          id,
        },
      },
    });
  }),

  mockEntityAsideUpdateSuppressionMutation(
    async ({ query: _query, variables }) => {
      await delay(50);
      const { entityId, whitelistFlag } = variables;

      mockEntity.isWhitelisted = !whitelistFlag;

      return HttpResponse.json({
        data: {
          __typename: 'Mutation',
          updateEntityWhitelist: {
            __typename: 'UpdateEntityWhitelist',
            data: {
              __typename: 'EntityInterface',
              id: entityId,
              isWhitelisted: !whitelistFlag,
            },
          },
        },
      });
    }
  ),

  mockEntityAsideLookbackOperationQuery(
    async ({ query: _query, variables: _variables }) => {
      await delay(50);
      return HttpResponse.json({
        data: {
          __typename: 'Query',
          insightConfig: {
            lookbackDays: anInsightConfig().lookbackDays,
            threshold: anInsightConfig().threshold,
            __typename: 'InsightConfig',
          },
        },
      });
    }
  ),

  mockEntityNetworkBlocksQueryOperationQuery(
    async ({ query: _query, variables: _variables }) => {
      await delay(50);
      return HttpResponse.json({
        data: {
          __typename: 'Query',
          networkBlocksByIp: mockNetworkBlocksByIp.map((block) => ({
            ...block,
          })),
        },
      });
    }
  ),

  mockEntityInputTagsQueryOperationQuery(
    async ({ query: _query, variables }) => {
      await delay(1000);
      const { id } = variables;

      return HttpResponse.json({
        data: {
          __typename: 'Query',
          entity: {
            id,
            tags: [...new Set(tagState)],
            __typename: 'EntityInterface',
          },
          entityTagOptions: [
            {
              value: 'Tag1',
              __typename: 'EntityTagOption',
            },
            {
              value: 'Tag2',
              __typename: 'EntityTagOption',
            },
            {
              value: 'Tag3',
              __typename: 'EntityTagOption',
            },
          ],
          tagSchemas: [
            {
              contentTypeEnums: [],
              freeform: false,
              label: 'Tactic',
              key: '_mitreAttackTactic',
              valueOptionObjects: [
                {
                  label: 'Data',
                  link: '#',
                  value: 'TA0001',
                  __typename: 'TagSchemaValueOption',
                },
                {
                  label: 'Defense Evasion',
                  link: '#',
                  value: 'TA0005',
                  __typename: 'TagSchemaValueOption',
                },
              ],
              __typename: 'TagSchema',
            },
            {
              contentTypeEnums: [],
              freeform: false,
              label: '_deviceGroup',
              key: '_deviceGroup',
              valueOptionObjects: [
                {
                  label: null,
                  link: '#',
                  value: 'Group 1',
                  __typename: 'TagSchemaValueOption',
                },
              ],
              __typename: 'TagSchema',
            },
            {
              contentTypeEnums: [],
              freeform: false,
              label: 'Office',
              key: 'Office',
              valueOptionObjects: [
                {
                  label: null,
                  link: '#',
                  value: 'Office 1',
                  __typename: 'TagSchemaValueOption',
                },
              ],
              __typename: 'TagSchema',
            },
          ],
        },
      });
    }
  ),

  // ENTITY TAGS UPDATE
  mockEntityInputTagsUpdateOperationMutation(
    async ({ query: _query, variables }) => {
      await delay(1000);
      const { id, tags } = variables;
      const isStringTag = typeof tags === 'string';
      const updatedTags = isStringTag ? [tags] : tags;

      tagState.push(...updatedTags);

      return HttpResponse.json({
        data: {
          __typename: 'Mutation',
          updateEntityTags: {
            data: {
              id,
              tags: updatedTags,
              __typename: 'EntityInterface',
            },
            __typename: 'UpdateEntityTags',
          },
        },
      });
    }
  ),

  // ENTITY TAGS DELETE
  mockEntityInputTagsDeleteOperationMutation(
    async ({ query: _query, variables }) => {
      await delay(3000);
      const { id, tags } = variables;

      tagState = tagState.filter((tag) => tag !== tags[0]);

      return HttpResponse.json({
        data: {
          __typename: 'Mutation',
          removeEntityTags: {
            data: {
              id,
              tags: tagState.filter((tag) => tag !== tags[0]),
              __typename: 'EntityInterface',
            },
            __typename: 'RemoveEntityTags',
          },
        },
      });
    }
  ),

  mockEntityInputCriticalityQueryOperationQuery(
    async ({ query: _query, variables }) => {
      await delay(1000);
      const { id } = variables;

      return HttpResponse.json({
        data: {
          __typename: 'Query',
          entity: {
            criticality: 'Low Risk',
            id,
            __typename: 'EntityInterface',
          },
          entityCriticalityConfigs: {
            objects: [
              {
                id: '1',
                name: 'Low Risk',
                severityExpression: 'severity / 2',
                __typename: 'EntityCriticalityConfig',
              },
              {
                id: '2',
                name: 'High Risk',
                severityExpression: 'severity * 2',
                __typename: 'EntityCriticalityConfig',
              },
            ],
            __typename: 'EntityCriticalityConfigPage',
          },
        },
      });
    }
  ),

  mockUpdateEntityInputCriticalityMutation(
    async ({ query: _query, variables }) => {
      await delay(1000);
      const { criticality, entityId } = variables;

      return HttpResponse.json({
        data: {
          __typename: 'Mutation',
          updateEntityCriticality: {
            __typename: 'UpdateEntityCriticality',
            data: {
              criticality,
              id: entityId,
              __typename: 'EntityInterface',
            },
          },
        },
      });
    }
  ),

  // READ ENTITY AUTOMATIONS
  mockEntityAutomationsQueryOperationQuery(
    async ({ query: _query, variables: _variables }) => {
      await delay(1500);
      return HttpResponse.json({
        data: {
          __typename: 'Query',
          automations: {
            objects: mockAutomations,
            __typename: 'AutomationPage',
          },
        },
      });
    }
  ),

  // GET ENTITY TYPE
  mockEntityTypeQueryOperationQuery(({ variables: { id } }) => {
    return HttpResponse.json({
      data: {
        __typename: 'Query',
        entity: {
          __typename: 'EntityInterface',
          id,
          entityType: id.replace('entity-', '_'),
        },
      },
    });
  }),

  // EXECUTE ENTITY AUTOMATION
  mockExecuteEntityAutomationOperationMutation(
    async ({ query: _query, variables: _variables }) => {
      await delay(1500);
      return HttpResponse.json({
        data: {
          __typename: 'Mutation',
          executeAutomation: {
            __typename: 'ExecuteAutomation',
            executionIds: ['1234'],
          },
        },
      });
    }
  ),

  // ENTITY FORM NOTES READ
  mockEntityFormNotesQueryOperationQuery(
    async ({ query: _query, variables: _variables }) => {
      await delay(1500);
      return HttpResponse.json({
        data: {
          __typename: 'Query',
          currentUser: {
            __typename: 'CurrentUser',
            displayName: 'Dash Prospers',
            id: '12345',
            role: 'red team',
          },
          entityNotes: mockNotes,
        },
      });
    }
  ),

  // ENTITY FORM NOTES UPDATE
  mockEntityFormNotesUpdateMutationMutation(
    async ({ query: _query, variables }) => {
      await delay(1500);
      const { note } = variables;

      const now = new Date(Date.now());

      const newNote: EntityNote = {
        author: {
          displayName: 'Dash Prospers',
          id: '12345',
          role: 'red team',
        } as User,
        id: `10${Math.round(Math.random() * 10)}`,
        lastUpdated: now,
        note,
        timestamp: now,
        __typename: 'EntityNote',
      };

      mockNotes = [...(mockNotes ?? []), newNote];

      return HttpResponse.json({
        data: {
          __typename: 'Mutation',
          addEntityNote: {
            data: newNote,
            __typename: 'AddEntityNote',
          },
        },
      });
    }
  ),

  // ENTITY FORM NOTES DELETE
  mockEntityFormNotesDeleteMutationMutation(
    async ({ query: _query, variables }) => {
      await delay(1500);
      const { entityNoteId } = variables;
      mockNotes = mockNotes?.filter((note: any) => note.id !== entityNoteId);

      return HttpResponse.json({
        data: {
          __typename: 'Mutation',
          deleteEntityNote: {
            ok: true,
            __typename: 'DeleteEntityNote',
          },
        },
      });
    }
  ),
];

export {
  mockEntityAsideQueryOperationQuery,
  mockEntityAsideUpdateSuppressionMutation,
};
